import { createClient } from '@supabase/supabase-js';
import { getLogger } from '../utils/logger';

// Create a namespaced logger
const logger = getLogger('Supabase');

// Check for required environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  logger.error('Missing VITE_SUPABASE_URL environment variable');
  throw new Error('Missing environment variable: VITE_SUPABASE_URL');
}

if (!supabaseAnonKey) {
  logger.error('Missing VITE_SUPABASE_ANON_KEY environment variable');
  throw new Error('Missing environment variable: VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Add a listener for auth state changes
supabase.auth.onAuthStateChange((event, session) => {
  logger.log('Auth state change event:', event);
  logger.log('Session exists:', !!session);
  
  if (event === 'SIGNED_OUT') {
    // Only clear auth-related data
    localStorage.removeItem('supabase.auth.token');
    logger.log('Cleared auth token from localStorage');
  }
  
  if (event === 'PASSWORD_RECOVERY') {
    logger.log('Password recovery event detected');
    // Redirect to reset password page if not already there
    if (!window.location.pathname.includes('/reset-password')) {
      window.location.href = '/reset-password';
    }
  }
});

// Helper function to get the current session
export async function getCurrentSession() {
  try {
    logger.log('Getting current session...');
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      logger.error('Error getting session:', error);
      return null;
    }
    
    logger.log('Session retrieved successfully:', !!session);
    return session;
  } catch (error) {
    logger.error('Exception in getCurrentSession:', error);
    return null;
  }
}

// Helper function to get the current user
export async function getCurrentUser() {
  try {
    logger.log('Getting current user...');
    const session = await getCurrentSession();
    const user = session?.user ?? null;
    logger.log('User exists:', !!user);
    return user;
  } catch (error) {
    logger.error('Exception in getCurrentUser:', error);
    return null;
  }
}

// Helper function to get user profile
export async function getUserProfile(userId: string) {
  try {
    logger.log('Getting user profile for ID:', userId);
    const { data, error } = await supabase
      .from('profiles')
      .select('name, avatar_url')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching user profile:', error);
      return null;
    }

    logger.log('User profile retrieved:', !!data);
    return data;
  } catch (error) {
    logger.error('Exception in getUserProfile:', error);
    return null;
  }
}

// Helper function to update user profile
export async function updateUserProfile(userId: string, updates: { name?: string; avatar_url?: string }) {
  try {
    logger.log('Updating user profile for ID:', userId, 'with updates:', updates);
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      logger.error('Error updating user profile:', error);
      throw error;
    }

    logger.log('User profile updated successfully');
    return data;
  } catch (error) {
    logger.error('Exception in updateUserProfile:', error);
    throw error;
  }
}